import { useMuiController } from "@sinch/components";
import { useCallback, useMemo } from "react";

import { MessageFormFields } from "./formSchema";
import { RecipientsOptions } from "./RecipientTag";
import { useUserGroups } from "./useUserGroups";

interface UseGroupedRecipientsParams {
  disabled?: boolean;
  /** When true, user groups are fetched and merged into the recipients value. */
  useGroups?: boolean;
}

/**
 * Unifies the `recipients` and `userGroups` form fields behind a single
 * autocomplete value. User groups are loaded on demand, kept out of the
 * dropdown, and routed back into their own form field on change.
 */
export const useGroupedRecipients = ({ disabled, useGroups }: UseGroupedRecipientsParams) => {
  const { field: recipientsField } = useMuiController<MessageFormFields, "recipients">({
    name: "recipients",
    disabled,
  });
  const { field: userGroupsField } = useMuiController<MessageFormFields, "userGroups">({
    name: "userGroups",
    disabled,
  });

  const { options: groupOptions } = useUserGroups({ skip: !useGroups });

  // Set of group ids, used to keep groups out of the dropdown and to route
  // changed values into the correct form field.
  const groupValues = useMemo(() => new Set(groupOptions.map(({ value }) => value)), [groupOptions]);

  // The autocomplete operates on the union of plain recipients and user groups.
  const value = useMemo<(string | number)[]>(
    () => [...recipientsField.value, ...userGroupsField.value],
    [recipientsField.value, userGroupsField.value]
  );

  // On change, split the selected values back into their respective form fields.
  const handleChange = useCallback(
    (newValue: (string | number)[] | null) => {
      const next = (newValue ?? []).map(String);
      recipientsField.onChange(next.filter((item) => !groupValues.has(item)));
      userGroupsField.onChange(next.filter((item) => groupValues.has(item)));
    },
    [groupValues, recipientsField, userGroupsField]
  );

  // Honour ids already selected as groups even before their options have loaded,
  // so a preselected group is never briefly mistaken for an unresolved recipient.
  const isGroup = useCallback(
    (optionValue: string | number) =>
      groupValues.has(optionValue) || userGroupsField.value.includes(String(optionValue)),
    [groupValues, userGroupsField.value]
  );

  // Search is performed server-side (useRecipients.onSearch), so the typed value
  // must not re-filter the fetched options on the client - that would hide valid
  // server matches. We only strip group options from the dropdown; groups are
  // still rendered as tags.
  const filterOptions = useCallback(
    (opts: RecipientsOptions[]) => opts.filter(({ value: optionValue }) => !groupValues.has(optionValue)),
    [groupValues]
  );

  return {
    recipientsField,
    recipients: recipientsField.value,
    value,
    groupOptions,
    handleChange,
    isGroup,
    filterOptions,
  };
};
